import { useEffect, useState } from 'react'
import { pdfjs } from 'react-pdf'

// Configure the worker to use the local version from node_modules
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

type PdfImageResult = {
  imageUrl: string | null
  width: number
  height: number
  isLoading: boolean
}

/**
 * Hook that converts a PDF to an image URL using canvas
 * This allows PDFs to be used with the ImageViewerWithOverlay component
 */
export function usePdfAsImage(
  pdfUrl: string | undefined,
  pageNumber = 1,
  scale = 2,
  totalPages = 1
): PdfImageResult {
  const [result, setResult] = useState<PdfImageResult>({
    imageUrl: null,
    width: 0,
    height: 0,
    isLoading: false,
  })

  useEffect(() => {
    let mounted = true
    let objectUrl: string | null = null

    if (!pdfUrl || pageNumber > totalPages) {
      setResult({
        imageUrl: null,
        width: 0,
        height: 0,
        isLoading: false,
      })
      return
    }

    // Set loading state when PDF URL changes
    setResult({
      imageUrl: null,
      width: 0,
      height: 0,
      isLoading: true,
    })

    async function convertPdfToImage() {
      try {
        const loadingTask = pdfjs.getDocument(pdfUrl)
        const pdf = await loadingTask.promise
        const page = await pdf.getPage(pageNumber)

        // Create canvas
        const viewport = page.getViewport({ scale })
        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')
        if (!context) return

        canvas.height = viewport.height
        canvas.width = viewport.width

        // Render PDF page to canvas
        await page.render({
          canvasContext: context,
          viewport: viewport,
        }).promise

        // Convert canvas to blob then to object URL
        canvas.toBlob((blob) => {
          if (!blob || !mounted) return
          objectUrl = URL.createObjectURL(blob)
          setResult({
            imageUrl: objectUrl,
            width: viewport.width,
            height: viewport.height,
            isLoading: false,
          })
        }, 'image/png')
      } catch (error) {
        console.error('Error converting PDF to image:', error)
        if (mounted) {
          setResult({
            imageUrl: null,
            width: 0,
            height: 0,
            isLoading: false,
          })
        }
      }
    }

    convertPdfToImage()

    return () => {
      mounted = false
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [pdfUrl, pageNumber, scale, totalPages])

  return result
}
